import type { FillAllocationDto, FillShareDto, ShortfallNoteDto } from '@pams/shared';
import { allocationAddresseeId } from './fillCursor';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [C4a §7] ONE SENTENCE FOR A SHORT-FALL, READ BY THE PREVIEW AND BY THE PRINTED ORDER.
 *
 *  ⚠️ IN `lib/` RATHER THAN IN EITHER PAGE'S `copy.ts`, because both render the SAME DTO and the
 *  preview copy's own rule is that neither surface imports the other's copy file. Two wordings of
 *  "asked for N, sent M" on the preview and the paper it previews is the second-vocabulary defect.
 *
 *  ⚠️ THE REASON IS PART OF THE SENTENCE, NOT A FOOTNOTE (§7c). A reason captured and then not
 *  printed makes the capture ceremonial, and it is the only part a commander can act on.
 *
 *  ⚠️ "SENT" IS THE LIVE NAMED COUNT. A name added after the division closed short raises it, and a
 *  share that has since been filled produces no note at all (the server omits it), so the paper never
 *  states a shortage that no longer exists.
 *
 *  ─── ⚠️ [amended C5 §7d] TWO KINDS, AND THEY MUST NOT READ ALIKE ────────────────────────────
 *
 *  `shortfallNotesFor` returns the DERIVED gap beside the DECLARED one now, and a commander acts
 *  differently on each: an `OPEN` gap is a division that can still be chased, a `DECLARED` one is a
 *  division that has already replied. A screen showing them alike sends somebody to ring an officer
 *  who has answered — which is the same class of untruth §3 is about, one grain down.
 *
 *  ⚠️ THE WORDS CARRY IT, NOT A COLOUR. This is a PRINTED document: it is photocopied, faxed and
 *  read under a streetlight, so hue is not a channel it has. `Still to come` and `Closed short` are
 *  two headings, and only the second states a reason — because only the second has one.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const shortfallSentence = (note: ShortfallNoteDto): string => {
  const counts = `${note.label}: asked for ${String(note.askedCount)}, sent ${String(note.sentCount)}, short by ${String(note.shortCount)}.`;
  /* ⚠️ `reason` is `null` on an OPEN note BY CONSTRUCTION — the server sets one only on a
     DECLARED one — so this cannot print `Reason: null` however the two are combined. */
  return note.kind === 'DECLARED' && note.reason !== null ? `${counts} Reason: ${note.reason}` : counts;
};

/**
 * ⚠️ TWO HEADINGS, BECAUSE THEY ARE TWO FACTS. `Short-fall` alone described the declared case and
 * was the only case that existed; §7d adds the derived one, and putting both under one heading is
 * what the split exists to prevent.
 */
export const SHORTFALL_HEADING = 'Closed short';
export const OPEN_GAP_HEADING = 'Still to come';

/** The heading a note belongs under — so a renderer cannot file one of them wrongly. */
export const shortfallHeadingOf = (kind: ShortfallNoteDto['kind']): string =>
  kind === 'DECLARED' ? SHORTFALL_HEADING : OPEN_GAP_HEADING;

/**
 * ⚠️ [C5b §3] NOTES GROUPED UNDER THEIR HEADINGS, IN THE ORDER THE SERVER SENT THEM.
 *
 * The preview headed every note `Notes` while the printed order it previews headed them by kind
 * (D11) — so a division that could still be chased and one that had already replied read alike on
 * one screen and apart on the other. One grouping, keyed by `shortfallHeadingOf`, so a renderer
 * cannot file a note under the wrong heading or invent a third.
 */
export const shortfallGroups = (
  notes: readonly ShortfallNoteDto[],
): { heading: string; notes: ShortfallNoteDto[] }[] => {
  const groups = new Map<string, ShortfallNoteDto[]>();
  for (const note of notes) {
    const heading = shortfallHeadingOf(note.kind);
    groups.set(heading, [...(groups.get(heading) ?? []), note]);
  }
  return [...groups].map(([heading, grouped]) => ({ heading, notes: grouped }));
};

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [P7A §4] NOT ASSIGNED IS NOT THE SAME AS SHORT — ON THE FILL SCREEN'S OWN ROWS.
 *
 *  The fill sheet's post dot, heading and rows read a declared `0 of 12` close exactly as they read
 *  an untouched `0 of 12` — while the sheet's own shares carried the reason and the screen displayed
 *  it nowhere. These three state the close on the ALLOCATION ROW, the addressee grain at which a
 *  division declares; the dot keeps saying the post is unstaffed, which is also true, and NO COLOUR
 *  MOVES ([C5b §3]: a declared close keeps its post red or amber; the lock lives on the footer).
 * ─────────────────────────────────────────────────────────────────────────────────
 */

/** The one empty map — a host that states the close elsewhere hands this, as it hands `FILL_CONTROLS_NONE`. */
export const NO_CLOSED_SHORT: ReadonlyMap<string, string> = new Map();

/**
 * The addressees that DECLARED their share short, and their words — keyed by the ADDRESSEE.
 *
 * ⚠️ THE PREDICATE IS `DutyFill`'s EXCUSED SET's, AND IT IS ONE DERIVATION NOW: `completedAt !== null`
 * AND `shortfallReason !== null`. Completion alone is also true of a share `autoCompleteFullShares`
 * closed because every slot was named, with no reason ([C3c.2]). ⚠️ `orgUnit`, NEVER `division` —
 * completion is per ADDRESSEE (FR-5.3.6b), so reading the division would mark every station under it
 * from one station's declaration.
 */
export const closedShortReasonsOf = (
  shares: readonly Pick<FillShareDto, 'orgUnit' | 'completedAt' | 'shortfallReason'>[],
): ReadonlyMap<string, string> =>
  new Map(
    shares.flatMap((share) =>
      share.completedAt !== null && share.shortfallReason !== null && share.orgUnit
        ? [[share.orgUnit.id, share.shortfallReason] as const]
        : [],
    ),
  );

/**
 * The reason an allocation row closed short — only while the row is STILL short. A row its addressee
 * has since filled has nothing to state, exactly as the printed order drops a note once the names
 * arrive. `null` for a row whose addressee said nothing.
 */
export const allocationClosedShort = (
  reasons: ReadonlyMap<string, string>,
  allocation: Pick<FillAllocationDto, 'unit' | 'division' | 'allocatedCount'>,
  named: number,
): string | null => {
  if (named >= allocation.allocatedCount) return null;
  const addressee = allocationAddresseeId(allocation);
  return addressee === null ? null : (reasons.get(addressee) ?? null);
};

/**
 * `Closed short · Reason: Two men on court duty` — the row's line, under the same heading the
 * preview, the record page and the paper use. ⚠️ It never says WHO declared: `completeShares` closes
 * every open share its caller can see, so a share may have been closed from above.
 */
export const closedShortRowNote = (reason: string): string => `${SHORTFALL_HEADING} · Reason: ${reason}`;
